import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import SpherePreview from '../components/SpherePreview';

const PRESET_COLORS = [
  { name: 'Weiß', hex: '#FFFFFF' },
  { name: 'Schwarz', hex: '#1a1a1a' },
  { name: 'Rot', hex: '#ef4444' },
  { name: 'Blau', hex: '#3b82f6' },
  { name: 'Grün', hex: '#22c55e' },
  { name: 'Gelb', hex: '#eab308' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Pink', hex: '#ec4899' },
  { name: 'Lila', hex: '#a855f7' },
  { name: 'Türkis', hex: '#14b8a6' },
  { name: 'Gold', hex: '#d4af37' },
  { name: 'Silber', hex: '#c0c0c0' },
];

const OPTIONS = [
  { id: 'natur', label: 'Nur Natur (unlackiert)', description: 'Rohes Styropor ohne Lackierung' },
  { id: 'hanging', label: 'Mit Abhängung', description: 'Inkl. Aufnahme zum Aufhängen' },
  { id: 'flat', label: 'Abgeflacht (Standfläche)', description: 'Flache Unterseite zum Aufstellen' },
  { id: 'ring', label: 'Mit Unterlegring', description: 'Stabiler Standring zum Aufstellen' },
  { id: 'a1', label: 'A1-Beschichtung', description: 'Nicht brennbar nach DIN 4102 A1' },
];

const ConfiguratorPage = () => {
  const navigate = useNavigate();
  const { addItem, itemCount } = useCart();
  
  const [config, setConfig] = useState({
    size: 500,
    color: '#FFFFFF',
    customColor: '',
    ralNcs: '',
    coating: '',
    options: [],
    quantity: 1,
    notes: '',
  });

  const handleSizeChange = (value) => {
    setConfig(prev => ({ ...prev, size: value[0] }));
  };

  const handleColorSelect = (hex) => {
    setConfig(prev => ({ ...prev, color: hex, customColor: '' }));
  };

  const handleCustomColorChange = (e) => {
    const hex = e.target.value;
    setConfig(prev => ({ ...prev, customColor: hex, color: hex }));
  };

  const handleOptionToggle = (optionId) => {
    setConfig(prev => {
      const newOptions = prev.options.includes(optionId)
        ? prev.options.filter(o => o !== optionId)
        : [...prev.options, optionId];
      return { ...prev, options: newOptions };
    });
  };

  const handleAddToCart = () => {
    const sphereConfig = {
      size_mm: config.size,
      color: config.color,
      coating: config.coating || null,
      options: config.options,
      quantity: config.quantity,
      notes: config.notes || null,
    };
    
    addItem(sphereConfig);
    toast.success('Ei zum Warenkorb hinzugefügt!', {
      description: `${config.size}mm, ${config.quantity}x`,
      action: {
        label: 'Zur Anfrage',
        onClick: () => navigate('/cart'),
      },
    });
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <p className="text-label text-primary mb-2">Schritt 1 von 2</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
              Ei konfigurieren
            </h1>
          </div>

          {/* Main Content - Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left: Controls */}
            <div className="lg:col-span-5 space-y-8">
              {/* Size Slider */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-label text-zinc-400">Höhe</Label>
                  <span className="font-heading text-2xl font-bold text-primary">{config.size} mm</span>
                </div>
                <Slider
                  value={[config.size]}
                  onValueChange={handleSizeChange}
                  min={500}
                  max={1500}
                  step={50}
                  className="py-4"
                  data-testid="size-slider"
                />
                <div className="flex justify-between text-xs text-zinc-500 mt-2">
                  <span>500 mm</span>
                  <span>1500 mm</span>
                </div>
              </div>

              {/* Color Selection */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <Label className="text-label text-zinc-400 block mb-4">Farbe</Label>
                <div className="grid grid-cols-6 gap-3 mb-4">
                  {PRESET_COLORS.map((c) => (
                    <Tooltip key={c.hex}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleColorSelect(c.hex)}
                          className={`color-option w-10 h-10 rounded-sm border-2 transition-all ${
                            config.color === c.hex && !config.customColor
                              ? 'border-primary scale-110'
                              : 'border-zinc-700 hover:border-zinc-500'
                          }`}
                          style={{ backgroundColor: c.hex }}
                          data-testid={`color-${c.name.toLowerCase()}`}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{c.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-zinc-400">Eigene Farbe:</Label>
                  <Input
                    type="color"
                    value={config.customColor || config.color}
                    onChange={handleCustomColorChange}
                    className="w-12 h-10 p-1 cursor-pointer bg-zinc-800 border-zinc-700"
                    data-testid="custom-color-picker"
                  />
                  <Input
                    type="text"
                    value={config.customColor || config.color}
                    onChange={(e) => handleCustomColorChange({ target: { value: e.target.value } })}
                    placeholder="#FFFFFF"
                    className="flex-1 h-10 bg-zinc-900 border-zinc-800 text-sm font-mono"
                    data-testid="color-hex-input"
                  />
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Label className="text-sm text-zinc-400">RAL/NCS:</Label>
                  <Input
                    type="text"
                    value={config.ralNcs || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, ralNcs: e.target.value }))}
                    placeholder="z.B. RAL 9010, NCS S 0500-N"
                    className="flex-1 h-10 bg-zinc-900 border-zinc-800 text-sm"
                    data-testid="ral-ncs-input"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <Label className="text-label text-zinc-400 block mb-4">Optionen</Label>
                <div className="space-y-3">
                  {OPTIONS.map((option) => (
                    <div
                      key={option.id}
                      className={`flex items-start gap-3 p-3 rounded-sm border transition-colors cursor-pointer ${
                        config.options.includes(option.id)
                          ? 'border-primary/50 bg-primary/5'
                          : 'border-zinc-800 hover:border-zinc-700'
                      }`}
                      onClick={() => handleOptionToggle(option.id)}
                      data-testid={`option-${option.id}`}
                    >
                      <Checkbox
                        checked={config.options.includes(option.id)}
                        onCheckedChange={() => handleOptionToggle(option.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="text-white font-medium">{option.label}</span>
                        <p className="text-xs text-zinc-500 mt-0.5">{option.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coating & Quantity */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">Beschichtung</Label>
                    <Input
                      value={config.coating}
                      onChange={(e) => setConfig(prev => ({ ...prev, coating: e.target.value }))}
                      placeholder="z.B. matt/Seidenmatt/Metallic"
                      className="h-12 bg-zinc-900 border-zinc-800"
                      data-testid="coating-input"
                    />
                  </div>
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">Menge</Label>
                    <Input
                      type="number"
                      min={1}
                      value={config.quantity}
                      onChange={(e) => setConfig(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="h-12 bg-zinc-900 border-zinc-800"
                      data-testid="quantity-input"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <Label className="text-label text-zinc-400 block mb-2">Anmerkungen</Label>
                <Textarea
                  value={config.notes}
                  onChange={(e) => setConfig(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Besondere Wünsche oder Anforderungen..."
                  className="min-h-[100px] bg-zinc-900 border-zinc-800 resize-none"
                  data-testid="notes-textarea"
                />
              </div>

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                className="w-full h-14 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90 btn-active text-lg"
                data-testid="add-to-cart-button"
              >
                <Plus className="mr-2 h-5 w-5" />
                Zum Warenkorb hinzufügen
              </Button>

              {itemCount > 0 && (
                <Button
                  onClick={() => navigate('/cart')}
                  variant="outline"
                  className="w-full h-12 border border-zinc-700 hover:border-primary hover:text-primary bg-transparent text-white uppercase tracking-wider rounded-sm"
                  data-testid="go-to-cart-button"
                >
                  Zur Anfrage ({itemCount} {itemCount === 1 ? 'Ei' : 'Eier'})
                </Button>
              )}
            </div>

            {/* Right: Preview */}
            <div className="lg:col-span-7 lg:sticky lg:top-24 lg:self-start">
              <div className="bg-zinc-900/30 border border-zinc-800 rounded-sm p-8 min-h-[500px] flex items-center justify-center">
                <SpherePreview
                  size={config.size}
                  color={config.color}
                  options={config.options}
                />
              </div>
              
              {/* Configuration Summary */}
              <div className="mt-6 bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <h3 className="text-label text-zinc-400 mb-4">Aktuelle Konfiguration</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Größe:</span>
                    <span className="ml-2 text-white font-medium">{config.size} mm</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-zinc-500">Farbe:</span>
                    <span
                      className="ml-2 w-4 h-4 rounded-sm inline-block"
                      style={{ backgroundColor: config.color }}
                    />
                    <span className="ml-1 text-white font-mono text-xs">{config.color}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Menge:</span>
                    <span className="ml-2 text-white font-medium">{config.quantity}x</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Optionen:</span>
                    <span className="ml-2 text-white font-medium">
                      {config.options.length > 0 ? config.options.length : 'Keine'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ConfiguratorPage;
